import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink, Shield, Check, AlertTriangle } from 'lucide-react';

import { SEOHead } from '../seo/SEOHead';
import {
  getToolBySlug,
  getRelatedTools,
  getCategoryById,
  SITE,
  type ToolDefinition,
} from '../../lib/toolRegistry';
import {
  buildWebApplicationLd,
  buildBreadcrumbLd,
  buildFAQPageLd,
  buildHowToLd,
} from '../../lib/seoJsonLd';
import { trackToolOpen } from '@devkit/analytics';

interface ToolLayoutProps {
  /** Tool slug — drives registry lookup and analytics. */
  slug: string;
  children: React.ReactNode;
}

/* -------------------------------------------------------------------------- */
/* Sub-blocks                                                                 */
/* -------------------------------------------------------------------------- */

function FAQItem({ faq, index }: { faq: ToolDefinition['faqs'][number]; index: number }) {
  return (
    <details
      className="bg-surface rounded-xl border border-slate-700/50 overflow-hidden group"
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
    >
      <summary
        className="px-6 py-4 cursor-pointer text-white hover:text-primary transition-colors list-none flex items-center justify-between gap-3"
        itemProp="name"
      >
        <span>{faq.question}</span>
        <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform group-open:rotate-90" />
      </summary>
      <div
        className="px-6 pb-4 text-slate-400 leading-relaxed"
        itemScope
        itemProp="acceptedAnswer"
        itemType="https://schema.org/Answer"
      >
        <p itemProp="text">{faq.answer}</p>
        <meta itemProp="position" content={String(index + 1)} />
      </div>
    </details>
  );
}

/* -------------------------------------------------------------------------- */
/* Main layout                                                              */
/* -------------------------------------------------------------------------- */

export default function ToolLayout({ slug, children }: ToolLayoutProps) {
  const tool = getToolBySlug(slug);
  const category = tool ? getCategoryById(tool.category) : undefined;
  const relatedTools = getRelatedTools(slug);

  // Track tool_open once per mount
  useEffect(() => {
    if (tool) trackToolOpen(tool.slug);
  }, [tool?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tool) {
    return (
      <div className="py-16 container mx-auto px-4 text-center">
        <h1 className="text-2xl font-bold text-white mb-2">Tool not found</h1>
        <p className="text-slate-400">The tool <code className="text-primary">{slug}</code> is not registered.</p>
        <Link to="/tools" className="text-primary hover:underline mt-4 inline-block">
          Browse all tools →
        </Link>
      </div>
    );
  }

  const url = `${SITE.url}/${tool.slug}`;
  const jsonLd = [
    buildWebApplicationLd({ tool, categoryName: category?.name ?? tool.category, url }),
    buildBreadcrumbLd([
      { name: 'Home', url: SITE.url },
      { name: 'Tools', url: `${SITE.url}/tools` },
      { name: category?.name ?? tool.category, url: `${SITE.url}/tools/${tool.category}` },
      { name: tool.name, url },
    ]),
    buildFAQPageLd(tool.faqs),
    buildHowToLd(tool.howToSteps, `How to use ${tool.name}`, tool.shortDescription),
  ];

  return (
    <div className="py-8 animate-fade-in">
      <SEOHead
        title={tool.seo.title}
        description={tool.seo.description}
        keywords={tool.seo.keywords}
        canonical={url}
        og={{ title: tool.seo.title, description: tool.seo.description, url, type: 'website' }}
        twitter={{ card: 'summary_large_image', title: tool.seo.title, description: tool.seo.description }}
        jsonLd={jsonLd}
      />

      <div className="container mx-auto px-4">
        {/* Breadcrumb (visible) */}
        <nav className="mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-slate-400">
            <li>
              <Link to="/" className="hover:text-white transition-colors">
                Home
              </Link>
            </li>
            <ChevronRight className="w-4 h-4" aria-hidden />
            <li>
              <Link to="/tools" className="hover:text-white transition-colors">
                Tools
              </Link>
            </li>
            <ChevronRight className="w-4 h-4" aria-hidden />
            <li>
              <Link to={`/tools/${tool.category}`} className="hover:text-white transition-colors capitalize">
                {category?.name ?? tool.category}
              </Link>
            </li>
            <ChevronRight className="w-4 h-4" aria-hidden />
            <li className="text-white" aria-current="page">
              {tool.name}
            </li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 text-balance">
            {tool.name}
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl leading-relaxed">
            {tool.shortDescription}
          </p>
          {tool.disclaimer && (
            <div className="mt-4 flex gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
              <p>{tool.disclaimer}</p>
            </div>
          )}
        </header>

        {/* Tool UI */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">{children}</div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {relatedTools.length > 0 && (
              <div className="bg-surface rounded-xl border border-slate-700/50 p-6">
                <h3 className="font-semibold text-white mb-4">Related Tools</h3>
                <ul className="space-y-2">
                  {relatedTools.map((rt) => (
                    <li key={rt.slug}>
                      <Link
                        to={`/${rt.slug}`}
                        className="flex items-start gap-2 text-sm text-slate-400 hover:text-white transition-colors group"
                      >
                        <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                        <span>
                          <span className="block text-slate-200">{rt.name}</span>
                          <span className="block text-xs text-slate-500 mt-0.5">{rt.shortDescription}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-surface rounded-xl border border-slate-700/50 p-6">
              <h3 className="font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/tools"
                    className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    All Tools
                  </Link>
                </li>
                <li>
                  <Link
                    to={`/tools/${tool.category}`}
                    className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    More {category?.name ?? tool.category}
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>

        {/* Long description */}
        <section className="mt-12 pt-8 border-t border-slate-800">
          <h2 className="text-2xl font-semibold text-white mb-4">About the {tool.name}</h2>
          <p className="text-slate-300 leading-relaxed max-w-4xl">{tool.longDescription}</p>
        </section>

        {/* Features */}
        {tool.features.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-semibold text-white mb-4">Features</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-4xl">
              {tool.features.map((f, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-slate-300 bg-surface/40 border border-slate-700/50 rounded-lg p-3"
                >
                  <Check className="w-5 h-5 flex-shrink-0 text-green-400 mt-0.5" aria-hidden />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* How to use */}
        {tool.howToSteps.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-semibold text-white mb-4">How to use the {tool.name}</h2>
            <ol className="space-y-3 max-w-4xl">
              {tool.howToSteps.map((step, i) => (
                <li
                  key={i}
                  className="flex gap-3 bg-surface/40 border border-slate-700/50 rounded-lg p-4"
                >
                  <span className="flex-shrink-0 w-7 h-7 bg-primary/20 text-primary text-sm font-semibold rounded-full flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-medium text-white">{step.name}</p>
                    <p className="text-slate-400 mt-1 text-sm leading-relaxed">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Examples */}
        {tool.examples.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-semibold text-white mb-4">Examples</h2>
            <div className="space-y-6 max-w-4xl">
              {tool.examples.map((ex, i) => (
                <article
                  key={i}
                  className="bg-surface/40 border border-slate-700/50 rounded-lg overflow-hidden"
                >
                  <header className="px-5 py-3 border-b border-slate-700/50">
                    <h3 className="font-semibold text-white">{ex.title}</h3>
                    {ex.description && (
                      <p className="text-sm text-slate-400 mt-1">{ex.description}</p>
                    )}
                  </header>
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="p-5 border-b md:border-b-0 md:border-r border-slate-700/50">
                      <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Input</p>
                      <pre className="text-sm text-slate-200 font-mono whitespace-pre-wrap break-words">
                        {ex.input}
                      </pre>
                    </div>
                    <div className="p-5">
                      <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Output</p>
                      <pre className="text-sm text-green-300 font-mono whitespace-pre-wrap break-words">
                        {ex.output}
                      </pre>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* FAQ */}
        {tool.faqs.length > 0 && (
          <section className="mt-12" itemScope itemType="https://schema.org/FAQPage">
            <h2 className="text-2xl font-semibold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3 max-w-4xl">
              {tool.faqs.map((f, i) => (
                <FAQItem key={i} faq={f} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* Privacy reinforcement */}
        <section className="mt-12">
          <div className="bg-surface/60 border border-slate-700/50 rounded-xl p-6 flex gap-4 max-w-4xl">
            <Shield className="w-8 h-8 flex-shrink-0 text-green-400" aria-hidden />
            <div>
              <h2 className="font-semibold text-white mb-1">Your data stays on your device</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                {tool.name} runs entirely in your browser. Nothing you paste is uploaded, stored,
                or shared with any server. You can verify this by opening DevTools and going
                offline — the tool still works.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}